import { loadPdfDocument } from "./utils";

type PdfDoc = Awaited<ReturnType<typeof loadPdfDocument>>;

/** Renders one page of an already-open pdf.js document to a small JPEG
 * data URL, scaled so its width is targetWidth. */
export async function renderPageThumbnail(pdf: PdfDoc, pageNumber: number, targetWidth: number): Promise<string> {
  const page = await pdf.getPage(pageNumber);
  try {
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: targetWidth / base.width });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported in this browser");
    await page.render({ canvasContext: ctx, viewport, canvas, intent: "print" }).promise;
    return canvas.toDataURL("image/jpeg", 0.7);
  } finally {
    page.cleanup();
  }
}

/** First-page thumbnail for a file, or null if it can't be rendered --
 * a missing thumbnail must never get in the way of the tool itself. */
export async function renderFirstPageThumbnail(file: File, targetWidth = 96): Promise<string | null> {
  try {
    const pdf = await loadPdfDocument(file);
    try {
      return await renderPageThumbnail(pdf, 1, targetWidth);
    } finally {
      void pdf.loadingTask.destroy();
    }
  } catch {
    return null;
  }
}
